import { createFileRoute } from "@tanstack/react-router";
import { CityLandingPage, buildCityLandingHead, type CityLandingConfig } from "@/components/CityLandingPage";
import heroImg from "@/assets/towns/pueblo.jpg";

const CONFIG: CityLandingConfig = {
  slug: "pueblo",
  name: "Pueblo",
  region: "Southern Colorado",
  heroEyebrow: "Pueblo Marketing Hub",
  heroTitle: { lead: "Pueblo marketing that respects how ", gold: "Pueblo actually buys", trail: "" },
  heroDescription:
    "Steel-town trades, downtown revitalization, agricultural businesses, and the Parkview healthcare corridor. Pueblo searches different than Denver — and we build for that.",
  intro:
    "Pueblo isn't Denver South. It's a working-class city with its own search behavior, driven by the steel mill, Parkview Health, the Riverwalk, and a tight-knit business community where reputation still spreads more on word-of-mouth than on Yelp. Our Pueblo work respects all of that.",
  neighborhoods: [
    "Belmont",
    "Mesa Junction",
    "Riverwalk / Downtown",
    "Pueblo West",
    "East Side",
    "South Side",
    "Bessemer",
    "Avondale",
  ],
  marketSnapshot:
    "Pueblo Map Pack competition is a fraction of Denver's — most categories show meaningful 3-pack movement at $800–$1,500/month. Lower competition means faster wins, but it also means owners who treat Pueblo like a side market lose to locals who show up consistently.",
  highlights: [
    {
      title: "Real wins on real-world budgets",
      body: "Pueblo SEO competition is 5–15 active competitors per category vs 50–200 in Denver. Most Pueblo categories rank top 3 in 60–90 days on consistent work.",
    },
    {
      title: "Trades + healthcare-aware content",
      body: "Pueblo's economy runs on trades, healthcare, and ag-adjacent services. We write content that speaks to actual Pueblo buyers — not generic Front Range fluff.",
    },
    {
      title: "South I-25 corridor coverage",
      body: "Most Pueblo service businesses also work Pueblo West, Avondale, Boone, and Colorado City. We map your real footprint and rank you across all of it.",
    },
  ],
  services: [
    { to: "/pueblo-seo", title: "Pueblo SEO", body: "Map Pack rankings for Belmont, Pueblo West, Riverwalk, and the south I-25 corridor at real-world budgets." },
    { to: "/web-design-pueblo-colorado", title: "Pueblo Web Design", body: "Fast, clear, mobile-first sites built for Pueblo buyer behavior and uneven cell coverage." },
    { to: "/colorado-springs-google-maps", title: "Google Maps optimization", body: "GBP optimization and review programs that win the tight Pueblo reputation economy." },
    { to: "/colorado-springs-ppc", title: "Google Ads management", body: "Pueblo PPC at budgets that actually make sense for Pueblo customer LTV." },
  ],
  learnArticleSlugs: [
    "/learn/local-seo-guide",
    "/learn/get-more-reviews",
    "/learn/google-business-profile",
    "/learn/service-area-pages",
    "/learn/marketing-budget",
    "/learn/seo-vs-ppc",
  ],
  learnIntro:
    "Pueblo's reputation economy means reviews and Map Pack consistency matter more here than in Denver. Start with these six articles before your next marketing dollar goes out.",
  heroImage: { src: heroImg, alt: "Historic Arkansas Riverwalk in Pueblo, Colorado", geo: { lat: 38.2544, lng: -104.6091, city: "Pueblo, CO" } },
  closingCta:
    "We'll audit your Pueblo Map Pack ranking, look at your reviews, check your service-area coverage, and quote a real Pueblo budget — typically $800–$1,500/month for meaningful movement.",
};

export const Route = createFileRoute("/pueblo")({
  head: () =>
    buildCityLandingHead(CONFIG, {
      metaTitle: "Pueblo Marketing Agency | SEO & Web Design | CWI",
      metaDescription:
        "Pueblo SEO, web design, and Google Ads built for how Pueblo actually buys. Belmont, Pueblo West, Riverwalk, and the south I-25 corridor. Lower budgets, faster wins.",
    }),
  component: () => <CityLandingPage config={CONFIG} />,
});
